import { Check, X } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * What a state means, not which colour it has (DESIGN.md of the customer portal: status
 * always carries text, red only for errors and final steps).
 *
 * - `success`: done, nothing to do ("Hinterlegt", "Angenommen", "Freigegeben")
 * - `warning`: still open, somebody has to act ("Fehlt", "Offen", "Pausiert")
 * - `danger`: broken or stopped by force ("Vom Netz", "Gesperrt")
 * - `active`: running now ("Live")
 * - `neutral`: settled and quiet ("Beendet", "Geplant")
 * - `outline`: not started or a marker ("Entwurf", "Test")
 */
export type StatusTone = "success" | "warning" | "danger" | "active" | "neutral" | "outline";

const TONES: Record<StatusTone, string> = {
  success: "border-transparent bg-success-soft text-success",
  warning: "border-transparent bg-notice-soft text-notice",
  danger: "border-transparent bg-destructive text-destructive-foreground",
  active: "border-transparent bg-primary text-primary-foreground",
  neutral: "border-transparent bg-secondary text-secondary-foreground",
  outline: "border-border text-foreground",
};

export function StatusBadge({
  tone,
  children,
  className,
}: {
  readonly tone: StatusTone;
  readonly children: ReactNode;
  readonly className?: string;
}): ReactElement {
  return (
    <Badge variant="outline" className={cn(TONES[tone], className)}>
      {children}
    </Badge>
  );
}

/**
 * Done or not as a sign (D94): a green tick or a muted cross, the words for screen readers and
 * on hover. For list columns where a badge per row was noise.
 */
export function Tick({
  done,
  label,
}: {
  readonly done: boolean;
  /** What the sign says, »hinterlegt« or »fehlt«. */
  readonly label: string;
}): ReactElement {
  const Sign = done ? Check : X;
  return (
    <span title={label} className="inline-flex">
      <Sign
        aria-hidden="true"
        className={cn("size-4", done ? "text-success" : "text-muted-foreground")}
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}
